// Ported from RaceCut (lib/racecut/caption-presets.ts). Keep the two in step when fixing
// a drawing or encoding bug; the export and the preview both depend on it.
/**
 * THE CAPTION PRESETS - each one is a whole, opinionated look: how the lyrics move, what the
 * letters are made of, and every knob already turned (font, size, case, colours). Flicking
 * through them shows a person everything that can change, before they touch a single knob.
 *
 * The motion stays bound to the preset. What the person tunes afterwards is the surface the
 * preset brought: font, size, case, the text colour - and the glow's colour if it glows, the
 * box's colour if it has one, the sung word's colour if the motion lights it up.
 *
 * Every length is in em of the font size, so a preset reads the same at 576p and 1080p. An
 * entrance that lasts "block" grows for as long as its phrase is on screen: a short block
 * moves fast, a long one slow, and the block's last frame is the entrance's last frame.
 */
import type { CaptionFont, TextCase } from "./layers";

export type Unit = "word" | "phrase";

/** What the word being sung gets: its own colour, or a box behind it. */
export type Emphasis = { kind: "color" | "box"; scale?: number };

export type Key = {
  at: number;
  scale?: number;
  opacity?: number;
  tracking?: number;
  blur?: number;
  rise?: number;
};

export type Entrance =
  | { kind: "keys"; secs: number | "block"; keys: Key[]; stagger?: number }
  /** Each word is typed out over its own timestamps. */
  | { kind: "typed" };

export type CaptionPresetId =
  | "pop"
  | "karaoke"
  | "karaoke-box"
  | "bounce"
  | "typewriter"
  | "neon"
  | "slide"
  | "grow"
  | "bold"
  | "pill";

export type CaptionPreset = {
  id: CaptionPresetId;
  unit: Unit;
  emphasis?: Emphasis;
  entrance: Entrance;
  stroke?: { width: number };
  glow?: { blur: number };
  pill?: { radius: number; pad: number };
  shadow?: { x: number; y: number };
  /** The knobs, as the preset turned them. The person's style starts as a copy. */
  knobs: PresetKnobs;
};

export type PresetKnobs = {
  font: CaptionFont;
  size: number;
  bold: boolean;
  italic: boolean;
  textCase: TextCase;
  /** The letters. */
  color: string;
  /** The word being sung, when the motion lights it up (its text, or its box). */
  accent: string;
  /** The glow, when the preset glows. */
  glow: string;
  /** The pill behind the phrase, when the preset has one. */
  pill: string;
};

const WHITE = "#FFFFFF";
const LIME = "#C4FF00";
const BLUE = "#2E4BFF";
const YELLOW = "#FFD400";
const TIKTOK_BLUE = "#1D4ED8";

/** The size slider's range, as a fraction of the reference size. */
export const CAPTION_SIZE = { smallest: 0.6, largest: 1.6, step: 0.1 };
const OUTLINE = { width: 0.06 };
/** A phrase on screen is several words at once: it starts as small as the slider goes, and growing it is taste. */
const BLOCK = CAPTION_SIZE.smallest;
const FADE_IN: Entrance = {
  kind: "keys",
  secs: 0.08,
  keys: [
    { at: 0, opacity: 0 },
    { at: 1, opacity: 1 },
  ],
};

const knobs = (turned: Partial<PresetKnobs>): PresetKnobs => ({
  font: "arial-black",
  size: 1,
  bold: true,
  italic: false,
  textCase: "as-is",
  color: WHITE,
  accent: LIME,
  glow: LIME,
  pill: BLUE,
  ...turned,
});

export const CAPTION_PRESETS: CaptionPreset[] = [
  {
    id: "pop",
    unit: "word",
    stroke: OUTLINE,
    entrance: {
      kind: "keys",
      secs: 0.11,
      keys: [
        { at: 0, scale: 0.55, opacity: 0 },
        { at: 0.36, opacity: 1 },
        { at: 1, scale: 1 },
      ],
    },
    knobs: knobs({}),
  },
  {
    id: "karaoke",
    unit: "phrase",
    stroke: OUTLINE,
    emphasis: { kind: "color", scale: 1.08 },
    entrance: FADE_IN,
    knobs: knobs({ size: BLOCK }),
  },
  {
    id: "karaoke-box",
    unit: "phrase",
    emphasis: { kind: "box" },
    entrance: FADE_IN,
    shadow: { x: 0, y: 0.04 },
    knobs: knobs({ textCase: "upper", size: BLOCK, accent: TIKTOK_BLUE }),
  },
  {
    id: "bounce",
    unit: "phrase",
    stroke: OUTLINE,
    emphasis: { kind: "color" },
    entrance: {
      kind: "keys",
      secs: 0.42,
      keys: [
        { at: 0, scale: 0.6, opacity: 0, rise: 0.4 },
        { at: 0.55, scale: 1.12, opacity: 1, rise: -0.06 },
        { at: 0.8, scale: 0.97, rise: 0.02 },
        { at: 1, scale: 1, rise: 0 },
      ],
    },
    knobs: knobs({ accent: YELLOW, size: BLOCK }),
  },
  {
    id: "typewriter",
    unit: "phrase",
    stroke: { width: 0.04 },
    entrance: { kind: "typed" },
    knobs: knobs({ font: "courier", size: BLOCK, bold: true }),
  },
  {
    id: "neon",
    unit: "phrase",
    stroke: { width: 0.025 },
    glow: { blur: 0.45 },
    entrance: {
      kind: "keys",
      secs: "block",
      keys: [
        { at: 0, opacity: 0, tracking: 0, blur: 0.25 },
        { at: 0.08, opacity: 1, blur: 0 },
        { at: 1, tracking: 0.14 },
      ],
    },
    knobs: knobs({ color: LIME, glow: LIME, size: BLOCK }),
  },
  {
    id: "slide",
    unit: "phrase",
    shadow: { x: 0, y: 0.05 },
    emphasis: { kind: "color" },
    entrance: {
      kind: "keys",
      secs: 0.28,
      keys: [
        { at: 0, opacity: 0, rise: 0.7 },
        { at: 1, opacity: 1, rise: 0 },
      ],
      stagger: 0.04,
    },
    knobs: knobs({ font: "trebuchet", size: BLOCK, bold: true }),
  },
  {
    id: "grow",
    unit: "phrase",
    stroke: OUTLINE,
    entrance: {
      kind: "keys",
      secs: "block",
      keys: [
        { at: 0, opacity: 0, scale: 0.86 },
        { at: 0.06, opacity: 1 },
        { at: 1, scale: 1.08 },
      ],
    },
    knobs: knobs({ size: BLOCK, textCase: "upper", color: YELLOW }),
  },
  {
    id: "bold",
    unit: "phrase",
    stroke: { width: 0.11 },
    shadow: { x: 0.04, y: 0.06 },
    emphasis: { kind: "color", scale: 1.05 },
    entrance: {
      kind: "keys",
      secs: 0.06,
      keys: [
        { at: 0, opacity: 0 },
        { at: 1, opacity: 1 },
      ],
    },
    knobs: knobs({ textCase: "upper", size: BLOCK, accent: YELLOW }),
  },
  {
    id: "pill",
    unit: "phrase",
    pill: { radius: 0.28, pad: 0.3 },
    emphasis: { kind: "color" },
    entrance: {
      kind: "keys",
      secs: 0.14,
      keys: [
        { at: 0, scale: 0.9, opacity: 0 },
        { at: 1, scale: 1, opacity: 1 },
      ],
    },
    knobs: knobs({ size: BLOCK, pill: BLUE, accent: LIME }),
  },
];

export function presetNamed(id: CaptionPresetId): CaptionPreset {
  return (
    CAPTION_PRESETS.find((preset) => preset.id === id) ?? CAPTION_PRESETS[0]
  );
}
